import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { money } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Receipt, QrCode, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PROVIDER_LABEL = { mercado_pago: "Mercado Pago", asaas: "Asaas" };

// Correções pós-homologação, item 25 — página pública do Faturamento SaaS
// (LOOTUS cobrando a empresa cliente), acessada pelo link compartilhado por
// e-mail/WhatsApp. Mesmo padrão do /invoice/:token (PublicInvoice.jsx), mas
// sem upload de comprovante — este documento é interno (LOOTUS <-> empresa
// cliente), não o faturamento operacional da empresa para os clientes dela.
export default function PublicSaasCharge() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL + "/api";
        const r = await fetch(`${base}/public/saas-charge/${token}`);
        if (!r.ok) throw new Error("Link inválido");
        setData(await r.json());
      } catch (e) { setErr(e.message); }
    })();
  }, [token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md bg-white rounded-xl border border-red-200 p-8 shadow-sm text-center">
        <h1 className="font-heading text-xl font-bold text-red-700">Link inválido</h1>
        <p className="text-slate-600 text-sm mt-2">{err}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando...</div>;

  const f = data.faturamento;
  const b = data.boleto;
  const isIntegrated = b && (b.modo === "mercado_pago" || b.modo === "asaas");
  const isSandbox = b && (b.environment === "test" || b.environment === "sandbox");
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch {
      toast.error(`Não foi possível copiar ${label.toLowerCase()}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-md bg-blue-600 text-white flex items-center justify-center font-heading font-bold">L</div>
            <div>
              <div className="font-heading font-bold text-slate-900">LOOTUS</div>
              <div className="text-xs text-slate-500">Faturamento da assinatura — {data.empresa?.nome}</div>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-4 flex flex-wrap justify-between items-start gap-4">
            <div>
              {/* Item 4 — referência comercial principal para o cliente
                  passa a ser o contrato permanente da empresa, não o
                  número interno da mensalidade (preservado abaixo, sem
                  destaque). */}
              <div className="text-sm font-heading font-semibold text-slate-900">
                Contrato nº {data.empresa?.contract_number_display || "-"}
              </div>
              <div className="text-sm text-slate-600 mt-1">Plano: <b>{f.plano_nome}</b></div>
              <div className="text-xs text-slate-500 mt-1">Competência {f.competencia_br} · Vencimento {f.vencimento_br}</div>
              <div className="text-xs text-slate-500 mt-1">{f.descricao}</div>
              <div className="text-[11px] text-slate-400 mt-1">Faturamento nº {f.numero}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500">Valor</div>
              <div className="font-heading font-bold text-3xl text-blue-700">{money(f.valor_final)}</div>
              <Badge className="mt-2 bg-blue-100 text-blue-800">{f.status_label || f.status}</Badge>
            </div>
          </div>
        </Card>

        {/* Correção PIX Asaas, item 13 — mesma UX de pagamento de
            PublicInvoice.jsx, aqui para a mensalidade LOOTUS (Asaas/
            Mercado Pago MASTER da Codexpy — nunca confundido com o
            Faturamento operacional do tenant). */}
        {b && b.instrumento_tipo === "pix" && isIntegrated && (
          <Card className="p-5">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2"><QrCode size={18} className="text-blue-600" /><div className="font-heading font-semibold">Pague via PIX</div></div>
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-800">{PROVIDER_LABEL[b.modo] || b.modo}</Badge>
                {isSandbox && <Badge className="bg-amber-100 text-amber-800">Ambiente de teste</Badge>}
              </div>
            </div>
            <div className="text-center space-y-3">
              {b.pix_qr_code_base64 ? (
                <img src={`data:image/png;base64,${b.pix_qr_code_base64}`} alt="QR Code PIX" className="mx-auto w-56 h-56 border border-slate-200 rounded-md" data-testid="public-saas-pix-qrcode" />
              ) : (
                <p className="text-sm text-slate-500">QR Code indisponível — use o Copia e Cola abaixo.</p>
              )}
              <div className="text-left text-sm bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1 max-w-sm mx-auto">
                <div><span className="text-slate-500">Valor:</span> <span className="font-medium text-slate-900">{money(f.valor_final)}</span></div>
                <div><span className="text-slate-500">Vencimento:</span> <span className="font-medium text-slate-900">{f.vencimento_br}</span></div>
                <div><span className="text-slate-500">Status:</span> <span className="font-medium text-slate-900">{b.status}</span></div>
              </div>
              {b.pix_code && (
                <div className="max-w-sm mx-auto text-left">
                  <div className="text-xs text-slate-500 mb-1">PIX Copia e Cola</div>
                  <div className="font-mono text-xs bg-slate-50 border border-slate-200 rounded-md p-3 break-all" data-testid="public-saas-pix-copia-cola">{b.pix_code}</div>
                </div>
              )}
              {b.pix_code && (
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => copyToClipboard(b.pix_code, "Código PIX")} data-testid="public-saas-pix-copiar">
                  <Copy size={16} /> Copiar código PIX
                </Button>
              )}
            </div>
          </Card>
        )}

        {b && b.instrumento_tipo === "boleto" && isIntegrated && (
          <Card className="p-5">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2"><Receipt size={18} className="text-blue-600" /><div className="font-heading font-semibold">Boleto</div></div>
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-800">{PROVIDER_LABEL[b.modo] || b.modo}</Badge>
                {isSandbox && <Badge className="bg-amber-100 text-amber-800">Ambiente de teste</Badge>}
              </div>
            </div>
            <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
              <div className="flex flex-wrap justify-between gap-3 text-sm">
                <div><b>Doc {b.numero}</b></div>
                <div><Badge className="bg-blue-100 text-blue-800">{b.status}</Badge> · <b>{money(f.valor_final)}</b></div>
              </div>
              {b.linha_digitavel && <div className="mt-2 font-mono text-xs text-slate-600 break-all">{b.linha_digitavel}</div>}
            </div>
            {b.boleto_url && (
              <Button className="mt-3 bg-blue-600 hover:bg-blue-700" onClick={() => window.open(b.boleto_url, "_blank", "noopener,noreferrer")} data-testid="public-saas-abrir-boleto">
                <ExternalLink size={16} /> Abrir boleto
              </Button>
            )}
          </Card>
        )}

        {b && !isIntegrated && (
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3"><Receipt size={18} className="text-blue-600" /><div className="font-heading font-semibold">Boleto</div></div>
            <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
              <div className="flex flex-wrap justify-between gap-3 text-sm">
                <div><b>Doc {b.numero}</b></div>
                {b.modo === "simulado" && <Badge className="bg-amber-100 text-amber-800">Modo simulado</Badge>}
              </div>
              <div className="mt-2 font-mono text-xs text-slate-600 break-all">{b.linha_digitavel}</div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Boleto em modo simulado — não representa um documento bancário registrado.</p>
          </Card>
        )}

        <div className="text-center text-xs text-slate-400">© {new Date().getFullYear()} · LOOTUS</div>
      </div>
    </div>
  );
}
